import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CallNotesEditor = ({ call, onSave, onClose }) => {
  const [notes, setNotes] = useState(call?.notes || '');
  const [disposition, setDisposition] = useState(call?.disposition || '');
  const [followUpDate, setFollowUpDate] = useState('');
  const [priority, setPriority] = useState(call?.priority || 'Orta');
  const [tags, setTags] = useState(call?.tags || []);
  const [newTag, setNewTag] = useState('');

  const dispositionOptions = [
    { value: 'Başarılı', label: 'Başarılı' },
    { value: 'Meşgul', label: 'Meşgul' },
    { value: 'Cevapsız', label: 'Cevapsız' },
    { value: 'Geri Arama', label: 'Geri Arama' },
    { value: 'Yanlış Numara', label: 'Yanlış Numara' }
  ];

  const priorityOptions = [
    { value: 'Düşük', label: 'Düşük' },
    { value: 'Orta', label: 'Orta' },
    { value: 'Yüksek', label: 'Yüksek' },
    { value: 'Acil', label: 'Acil' }
  ];

  const commonTags = [
    'Teknik Destek',
    'Satış',
    'Şikayet',
    'Bilgi Talebi',
    'Ödeme',
    'İptal',
    'Yenileme',
    'Geri Arama Gerekli'
  ];

  const handleAddTag = (tag) => {
    if (tag && !tags?.includes(tag)) {
      setTags([...tags, tag]);
    }
    setNewTag('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags?.filter(tag => tag !== tagToRemove));
  };

  const handleSave = () => {
    const updatedCall = {
      ...call,
      notes,
      disposition,
      followUpDate,
      priority,
      tags,
      lastModified: new Date()?.toISOString()
    };
    onSave(updatedCall);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Düşük': 'bg-green-100 text-green-800',
      'Orta': 'bg-yellow-100 text-yellow-800',
      'Yüksek': 'bg-orange-100 text-orange-800',
      'Acil': 'bg-red-100 text-red-800'
    };
    return colors?.[priority] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-elevated max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Arama Notları Düzenle</h3>
              <p className="text-sm text-slate-600">
                {call?.customerName} - {call?.date} {call?.time}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              iconName="X"
            />
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Call Information */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-slate-900 mb-3">Arama Bilgileri</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Müşteri:</span>
                <div className="font-medium text-slate-900">{call?.customerName}</div>
              </div>
              <div>
                <span className="text-slate-500">Telefon:</span>
                <div className="font-medium text-slate-900 font-mono">{call?.phoneNumber}</div>
              </div>
              <div>
                <span className="text-slate-500">Süre:</span>
                <div className="font-medium text-slate-900">{call?.duration}</div>
              </div>
              <div>
                <span className="text-slate-500">Temsilci:</span>
                <div className="font-medium text-slate-900">{call?.agentName}</div>
              </div>
              <div>
                <span className="text-slate-500">Arama ID:</span>
                <div className="font-medium text-slate-900">{call?.callId}</div>
              </div>
              <div>
                <span className="text-slate-500">Tür:</span>
                <div className="font-medium text-slate-900">{call?.type}</div>
              </div>
            </div>
          </div>

          {/* Disposition and Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Arama Sonucu"
              options={dispositionOptions}
              value={disposition}
              onChange={setDisposition}
              required
            />
            <Select
              label="Öncelik"
              options={priorityOptions}
              value={priority}
              onChange={setPriority}
            />
          </div>

          {/* Follow-up Date */}
          <Input
            type="date"
            label="Takip Tarihi"
            description="Gerekirse müşteriyle tekrar iletişim kurulacak tarih"
            value={followUpDate}
            onChange={(e) => setFollowUpDate(e?.target?.value)}
          />

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Etiketler
            </label>
            
            {/* Current Tags */}
            {tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {tags?.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-blue-600"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add New Tag */}
            <div className="flex items-center space-x-2 mb-3">
              <Input
                type="text"
                placeholder="Yeni etiket ekle..."
                value={newTag}
                onChange={(e) => setNewTag(e?.target?.value)}
                onKeyPress={(e) => {
                  if (e?.key === 'Enter') {
                    e?.preventDefault();
                    handleAddTag(newTag);
                  }
                }}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddTag(newTag)}
                iconName="Plus"
                disabled={!newTag?.trim()}
              >
                Ekle
              </Button>
            </div>

            {/* Common Tags */}
            <div>
              <span className="text-xs text-slate-500 mb-2 block">Sık Kullanılan Etiketler:</span>
              <div className="flex flex-wrap gap-2">
                {commonTags?.filter(tag => !tags?.includes(tag))?.map((tag, index) => (
                  <button
                    key={index}
                    onClick={() => handleAddTag(tag)}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors duration-150"
                  >
                    <Icon name="Plus" size={10} className="mr-1" />
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Arama Notları
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              placeholder="Arama sırasında alınan notları, müşteri taleplerini ve önemli detayları buraya yazın..."
              rows={6}
              className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none"
            />
            <div className="mt-1 text-xs text-slate-500">
              {notes?.length}/1000 karakter
            </div>
          </div>

          {/* Character Count and Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
              <div>
                <h5 className="text-sm font-medium text-blue-900 mb-1">Not Yazma İpuçları</h5>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• Müşterinin ana talebini ve verilen çözümü belirtin</li>
                  <li>• Önemli müşteri bilgilerini (tercihleri, özel durumlar) kaydedin</li>
                  <li>• Takip gerektiren konuları açıkça belirtin</li>
                  <li>• Gelecekteki aramalarda faydalı olacak detayları ekleyin</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-500">
              Son güncelleme: {call?.lastModified ? new Date(call.lastModified)?.toLocaleString('tr-TR') : 'Henüz güncellenmedi'}
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
              >
                İptal
              </Button>
              <Button
                variant="default"
                onClick={handleSave}
                iconName="Save"
                iconPosition="left"
              >
                Kaydet
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallNotesEditor;